<template>
  <aside :class="['relative bg-gray-800 flex flex-col h-screen sticky top-0 border-l border-gray-700 shrink-0 transition-width duration-200', isCollapsed ? 'w-2' : 'w-96']">

    <!-- Vertical edge toggle trigger -->
    <button
      @click="toggleCollapse"
      :aria-label="isCollapsed ? $t('sidebar.expand') : $t('sidebar.collapse')"
      :aria-pressed="isCollapsed.toString()"
      :aria-expanded="(!isCollapsed).toString()"
      v-tooltip.left="isCollapsed ? $t('sidebar.expand') : $t('sidebar.collapse')"
      type="button"
      class="absolute -left-2 top-0 h-full w-2 z-10 bg-gray-800 hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center cursor-pointer group"
    >
      <div class="relative flex items-center justify-center">
        <svg
          :width="10"
          :height="22"
          viewBox="0 0 10 22"
          class="text-gray-800 group-hover:text-gray-600 transition-colors duration-200"
          :class="isCollapsed ? 'rotate-180 -translate-x-1.5' : 'translate-x-1.5'"
        >
          <polygon points="0,0 10,11 0,22" fill="currentColor" />
        </svg>
        <span
          class="material-icons absolute !text-[22px] text-white pointer-events-none"
          :class="isCollapsed ? '-translate-x-0.5' : 'translate-x-0.5'"
          aria-hidden="true"
          >
          {{ isCollapsed ? 'arrow_left' : 'arrow_right' }}
        </span>
      </div>
    </button>

    <!-- Header -->
    <div v-show="!isCollapsed" class="flex items-center px-4 py-3 border-b border-gray-700">
      <span class="material-icons text-gray-400 mr-4 !text-[18px]">chat</span>
      <div class="flex-1 min-w-0">
        <template v-if="!inTranscriptViewingMode">
          <h2 class="text-sm font-semibold text-white uppercase tracking-wide">{{ $t('botEditor.chatSidebar.title') }}</h2>
        </template>
        <template v-else>
          <h2 class="text-xs font-semibold text-gray-300 uppercase tracking-wide leading-tight">{{ $t('botEditor.chatSidebar.transcriptMode.viewingTitle') }}</h2>
          <div
            v-tooltip.bottom="!transcriptCopied ? $t('botEditor.transcripts.copyIdTooltip') : undefined"
            class="flex items-center gap-1.5 mt-0.5 w-fit cursor-pointer group"
            @click="copyTranscriptId"
          >
            <span
              class="font-mono text-xs transition-colors truncate"
              :class="transcriptCopied ? 'text-cyan-400' : 'text-gray-500 group-hover:text-gray-300'"
            >
              {{ transcriptCopied ? $t('botEditor.transcripts.copiedId') : 'ID: ' + activeTranscriptConvId }}
            </span>
            <span
              class="material-icons !text-[13px] transition-colors shrink-0 mb-0.5"
              :class="transcriptCopied ? 'text-cyan-400' : 'text-gray-500 group-hover:text-gray-300'"
            >{{ transcriptCopied ? 'check' : 'content_copy' }}</span>
          </div>
        </template>
      </div>
      <Button
        v-show="!inTranscriptViewingMode"
        @click="resetConversation"
        text
        severity="secondary"
        size="small"
        v-tooltip.bottom="$t('botEditor.chatSidebar.newConversationTooltip')"
        :aria-label="$t('botEditor.chatSidebar.newConversationAriaLabel')"
      >
        <span class="material-icons !text-[18px]" aria-hidden="true">add</span>
      </Button>
    </div>

    <!-- Transcript-loaded flash overlay — fixed so sticky/scroll doesn't affect it -->
    <div v-if="transcriptFlash" class="fixed inset-y-0 right-0 w-96 pointer-events-none z-50 transcript-loaded-flash" />

    <!-- Deep Chat message area -->
    <div v-show="!isCollapsed" ref="deepChatWrapRef" class="flex-1 min-h-0 overflow-hidden" />

    <!-- Processing documents banner -->
    <div
      v-if="!inTranscriptViewingMode"
      v-show="!isCollapsed && processingCount > 0"
      class="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border-t border-amber-500/20 text-amber-300 text-xs shrink-0"
    >
      <span class="relative flex h-2 w-2 shrink-0">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
        <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
      </span>
      <span>{{ $t('botEditor.chatSidebar.processingDocuments', { count: processingCount }, processingCount) }}</span>
    </div>
    <!-- Close transcript button container -->
    <div
      v-else
      v-show="!isCollapsed"
      class="flex items-center gap-2 px-3 py-1.5 border-t border-gray-700 shrink-0"
    >
      <Button
        severity="secondary"
        size="small"
        @click="handleCloseTranscript"
        class="w-full"
      >
        <span class="material-icons !text-[16px]">close</span>
        {{ $t('botEditor.chatSidebar.transcriptMode.closeButton') }}
      </Button>
    </div>

    <!-- Input footer -->
    <footer v-show="!isCollapsed && !inTranscriptViewingMode" class="p-3 bg-gray-900 border-t border-gray-700">
      <!-- Screen-reader announcement of the agent "typing" state (dots are visual-only) -->
      <div class="sr-only" role="status" aria-live="polite">{{ agentTyping ? $t('chatWidget.agentTyping') : '' }}</div>
      <!-- Pending files chips -->
      <div v-if="pendingFiles.length" class="flex flex-wrap gap-1 mb-2">
        <span
          v-for="(file, idx) in pendingFiles"
          :key="file.name + ':' + idx"
          class="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-700 text-gray-200 rounded text-xs max-w-[160px]"
        >
          <span class="material-icons !text-[12px] shrink-0 text-gray-400">insert_drive_file</span>
          <span class="truncate">{{ file.name }}</span>
          <button
            type="button"
            class="shrink-0 text-gray-400 hover:text-white focus:outline-none focus:text-white ml-0.5"
            :aria-label="$t('chatWidget.removeFile')"
            @click="removeFile(idx)"
          >
            <span class="material-icons !text-[12px]">close</span>
          </button>
        </span>
      </div>
      <form ref="footerFormRef" @submit.prevent="sendMessage" class="flex items-end gap-2">
        <!-- Hidden file input -->
        <input
          ref="fileInputRef"
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          class="hidden"
          @change="onFilesSelected"
        />
        <input
          ref="inputRef"
          v-model="messageText"
          type="text"
          :placeholder="agentTyping ? $t('chatWidget.agentTyping') : $t('chatWidget.inputPlaceholder')"
          :disabled="isSending || formPending || agentTyping"
          class="flex-1 px-3 py-2 bg-gray-700 text-white rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed h-10"
        />
        <!-- Attach button. A disabled button gets no hover events (and the "too many files"
             tooltip only applies while it is disabled), so the tooltip sits on a wrapper span
             and pointer-events-none lets the pointer through to it while the button is disabled -->
        <span
          v-tooltip.top="pendingFiles.length >= MAX_FILES ? $t('chatWidget.tooManyFiles', { max: MAX_FILES }) : $t('chatWidget.attachFile')"
          class="inline-flex shrink-0"
        >
          <Button
            type="button"
            :disabled="isSending || formPending || agentTyping || pendingFiles.length >= MAX_FILES"
            severity="secondary"
            size="small"
            class="h-10 shrink-0 disabled:pointer-events-none"
            :aria-label="$t('chatWidget.attachFile')"
            @click="fileInputRef?.click()"
          >
            <span class="material-icons" aria-hidden="true">attach_file</span>
          </Button>
        </span>
        <Button
          type="submit"
          :disabled="(!messageText.trim() && !pendingFiles.length) || isSending || formPending || agentTyping"
          severity="primary"
          size="small"
          class="h-10 shrink-0"
        >
          <span class="material-icons">send</span>
        </Button>
      </form>
    </footer>
  </aside>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useTranscriptViewer } from '../../composables/useTranscriptViewer';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { useRoute } from 'vue-router';
import { useIncomingMessages, useDocumentEvents } from '../../composables/useWebSocket';
import api from '../../services/api';
import rawCrafterCss from '../../crafter-chatroom-markdown.css?raw';
import { copyToClipboard } from '../../utils/clipboard';
import { loadDeepChat } from '../../utils/deepChat';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import chatService, { conversationTokenHeaders } from '../../services/chatService';
import * as storageApi from '../../services/storageApiService';
import { runCustomScriptSandbox } from '../../utils/customScriptSandbox';
import { collectFormValues, addArrayRow, removeArrayRow } from '../../utils/formArray';
import { clearFieldError, formErrorLabels } from '../../utils/formValidation';
import { wireAddressField } from '../../utils/addressAutocomplete';
import { wireSearchSelectField, makeSearchFn } from '../../utils/searchSelect';

const { t } = useI18n();
const toast = useToast();
const route = useRoute();
const { activeTranscriptConvId, transcriptMessages, closeTranscript } = useTranscriptViewer();
const workspaceId = computed(() => route.params.workspaceId);

const props = defineProps({
  botId: {
    type: String,
    required: true
  }
});

// Hardcoded style config — not from the user's widgetConfig
const SIDEBAR_STYLE = {
  userBubbleColor: '#2563eb',
  botBubbleColor:  '#374151',
  backgroundColor: '#1f2937'
};

// Injected into Deep Chat's shadow DOM so a form-bearing bubble claims the available
// width (instead of shrink-wrapping ~250px), letting the form's responsive grid flow
// into columns. Keep in sync with FORM_BUBBLE_CSS in
// apps/backend/src/services/flow/utils/formHtml.js (the widget injects the same rule).
const FORM_BUBBLE_CSS = '.message-bubble:has(.newc-form){width:min(100%,1060px)!important;}';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const deepChatWrapRef = ref(null);
const inputRef        = ref(null);
const footerFormRef   = ref(null);
const fileInputRef    = ref(null);
const messageText     = ref('');
const pendingFiles    = ref([]);
const isSending       = ref(false);
const formPending     = ref(false);
const agentTyping     = ref(false);
const conversationIdRef = ref(null);
const conversationTokenRef = ref(null);
// Imposta prima il token: il watch su conversationIdRef fa il join della room e lo legge.
function setConversation(id, token) {
  if (token !== undefined) conversationTokenRef.value = token;
  conversationIdRef.value = id;
}
const tokenHeaders = () => conversationTokenHeaders(conversationTokenRef.value);

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function onFilesSelected(event) {
  const selected = Array.from(event.target.files || []);

  // Check for files exceeding size limit
  const tooLarge = selected.filter(f => f.size > MAX_FILE_SIZE);
  tooLarge.forEach(f => {
    toast.add({
      severity: 'warn',
      summary: t('chatWidget.fileRejectedTitle'),
      detail: t('chatWidget.fileTooLarge', { name: f.name }),
      life: 4000
    });
  });

  const valid = selected.filter(f => f.size <= MAX_FILE_SIZE);
  const combined = [...pendingFiles.value, ...valid];

  // Check for cap exceeded
  if (combined.length > MAX_FILES) {
    toast.add({
      severity: 'warn',
      summary: t('chatWidget.fileRejectedTitle'),
      detail: t('chatWidget.tooManyFiles', { max: MAX_FILES }),
      life: 4000
    });
  }

  pendingFiles.value = combined.slice(0, MAX_FILES);
  // Reset the input so the same file can be re-selected if removed
  event.target.value = '';
}

function removeFile(idx) {
  pendingFiles.value = pendingFiles.value.filter((_, i) => i !== idx);
}

// Script della chatroom (custom + test) eseguiti in anteprima a ogni conversazione.
const widgetScripts = { customScript: '', testScript: '' };
let activeSandbox = null;

async function loadWidgetScripts() {
  try {
    const res = await api.get(`/workspaces/${workspaceId.value}/bots/${props.botId}`);
    const wc = res.data?.widgetConfig || res.data?.data?.widgetConfig || {};
    widgetScripts.customScript = typeof wc.customScript === 'string' ? wc.customScript : '';
    widgetScripts.testScript = typeof wc.testScript === 'string' ? wc.testScript : '';
  } catch {
    widgetScripts.customScript = '';
    widgetScripts.testScript = '';
  }
}

const isCollapsed     = ref(false);
const processingCount = ref(0);
const snapshotConversationId = ref(null);
const snapshotConversationToken = ref(null);
const inTranscriptViewingMode = computed(() => activeTranscriptConvId.value !== null);
const transcriptCopied = ref(false);
const transcriptFlash  = ref(false);

const copyTranscriptId = async () => {
  if (!activeTranscriptConvId.value) return;
  await copyToClipboard(activeTranscriptConvId.value);
  transcriptCopied.value = true;
  setTimeout(() => { transcriptCopied.value = false; }, 1500);
};
const _processingIds  = new Set();

function _setDocStatus(id, status) {
  if (status === 'processing') {
    _processingIds.add(id);
  } else {
    _processingIds.delete(id);
  }
  processingCount.value = _processingIds.size;
}

// TODO: if the number of storages/files per bot grows significantly, replace
// this fan-out fetch with a dedicated backend endpoint that returns only the
// in-progress count for a given bot, avoiding N+1 requests.
async function loadProcessingDocs() {
  try {
    const storages = await storageApi.listStorages(workspaceId.value, props.botId);
    const fileLists = await Promise.all(
      storages.map(s => storageApi.listFiles(workspaceId.value, props.botId, s._id).catch(() => []))
    );
    _processingIds.clear();
    processingCount.value = 0;
    for (const files of fileLists) {
      for (const file of files) _setDocStatus(file._id, file.status);
    }
  } catch {
    // Fail silently — banner is best-effort
  }
}

useDocumentEvents(props.botId, (doc) => _setDocStatus(doc._id, doc.status));

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
  try {
    localStorage.setItem('chatSidebarCollapsed', isCollapsed.value ? 'true' : 'false');
  } catch {}
};

let chatEl = null;
let pendingSelectedOptions = [];
let welcomeSent = false;

// In chat è attivo al massimo un form: l'ultimo, e solo finché è da compilare.
// Gli altri restano visibili ma inerti, anche quando la chat riparte dallo
// storico; il blocco dell'input segue solo quel form. Stessa regola del widget
// (widgetController, blocco FORM ATTIVO).
let activeFormId = null;
function formIdOf(m) {
  if (m && m._id) return String(m._id);
  const match = /data-form-id="([^"]+)"/.exec((m && m.text) || '');
  return match ? match[1] : null;
}
function deactivateForm(form) {
  if (!form || form.classList.contains('newc-form-inactive')) return;
  form.classList.add('newc-form-inactive');
  form.querySelectorAll('input, select, textarea, button').forEach(el => { el.disabled = true; });
  form.style.opacity = '0.6';
  form.style.pointerEvents = 'none';
}
function syncForms() {
  formPending.value = !!activeFormId;
  const root = chatEl && chatEl.shadowRoot;
  if (!root) return;
  root.querySelectorAll('form.newc-form').forEach(f => { if (f.dataset.formId !== activeFormId) deactivateForm(f); });
}
function setActiveForm(id) {
  activeFormId = id || null;
  syncForms();
  setTimeout(syncForms, 0);
}
// Invio o annullamento: la chat si libera solo se era il form attivo.
function closeForm(form) {
  deactivateForm(form);
  if (form && form.dataset.formId === activeFormId) setActiveForm(null);
}

// Chat in movimento: ogni messaggio aggiunto o bolla di attesa che compare o sparisce
// fa scorrere il contenuto, e un click in quell'istante cade sul bottone che si è appena
// spostato sotto il puntatore. Per un attimo dopo ogni cambiamento i click sulle
// risposte rapide si ignorano. Stessa regola del widget (widgetController, blocco
// CHAT IN MOVIMENTO).
const CHAT_ASSESTAMENTO_MS = 450;
let ultimoCambioChat = 0;
let osservatoreChat = null;
const chatInMovimento = () => Date.now() - ultimoCambioChat < CHAT_ASSESTAMENTO_MS;
// Solo nodi aggiunti o tolti: hover e disattivazione dei bottoni cambiano lo stile.
function osservaCambiChat(el) {
  if (osservatoreChat) osservatoreChat.disconnect();
  osservatoreChat = null;
  if (!el?.shadowRoot || typeof MutationObserver === 'undefined') return;
  osservatoreChat = new MutationObserver(() => { ultimoCambioChat = Date.now(); });
  osservatoreChat.observe(el.shadowRoot, { childList: true, subtree: true });
}
// Una domanda con risposte rapide è la risposta del turno: la bolla di attesa va tolta
// prima che compaia, altrimenti sparisce alla chiusura del turno e sposta i bottoni.
const haRisposteRapide = (html) => /newc-question-(buttons|select|multiselect)/.test(html || '');

// Bolla di attesa ("sta scrivendo"). In toggle-mode toggle() è un vero flip di stato:
// lo stato va tracciato perché mostra/nascondi siano idempotenti. Senza, nascondere la
// bolla all'arrivo di una domanda con bottoni la farebbe ricomparire alla chiusura del
// turno. Stessa logica del widget (setLoadingBubble).
let loadingBubbleShown = false;
function setLoadingBubble(show) {
  show = !!show;
  if (show === loadingBubbleShown || typeof chatEl?.displayLoadingBubble?.toggle !== 'function') return;
  try {
    chatEl.displayLoadingBubble.toggle(show ? { role: 'ai' } : undefined);
    loadingBubbleShown = show;
  } catch (e) { /* noop */ }
}

// Listen for messages pushed from webhook flows
let agentTypingTimeout = null;
useIncomingMessages(conversationIdRef, (data) => {
  if (!chatEl || !data) return;
  const atts = Array.isArray(data.attachments) ? data.attachments : [];
  if (atts.length > 0) {
    const files = atts.map(a => ({
      src: a.url,
      name: a.filename || 'file',
      type: (a.mimetype || '').startsWith('image') ? 'image' : 'file',
    }));
    chatEl.addMessage(data.content ? { files, text: data.content, role: 'ai' } : { files, role: 'ai' });
    return;
  }
  if (!data.content) return;
  if (data.type === 'html' || data.type === 'form') {
    if (data.type === 'html' && haRisposteRapide(data.content)) setLoadingBubble(false);
    chatEl.addMessage({ html: sanitizeHtml(data.content), role: 'ai' });
    if (data.type === 'form') setActiveForm(formIdOf({ _id: data.messageId, text: data.content }));
  } else {
    chatEl.addMessage({ text: data.content, role: 'ai' });
  }
}, (isTyping) => {
  // agent:typing — emitted around triggerAgent responses (e.g. after a form submit).
  // Show the loading bubble (same mechanism as user turns) and disable the footer input.
  if (!chatEl) return;
  clearTimeout(agentTypingTimeout);
  if (isTyping) {
    agentTyping.value = true;
    setLoadingBubble(true);
    // Safety net: if the `false` is lost (reconnection), hide the bubble and re-enable input.
    agentTypingTimeout = setTimeout(() => {
      agentTyping.value = false;
      setLoadingBubble(false);
    }, 45000);
  } else {
    agentTyping.value = false;
    setLoadingBubble(false);
  }
}, conversationTokenRef);

function escHtml(s) {
  if (!s) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Used when inserting message text into an HTML bubble (e.g. transcript view).
// marked converts markdown to HTML; DOMPurify strips any executable content.
function renderContent(text) {
  if (!text) return '';
  return DOMPurify.sanitize(marked.parse(text));
}

function buildDeepChat() {
  const el = document.createElement('deep-chat');
  el.style.cssText = 'width:100%;height:100%;display:block;border:none;border-radius:0;';

  el.chatStyle = { backgroundColor: SIDEBAR_STYLE.backgroundColor };

  el.messageStyles = {
    default: {
      user: { bubble: { backgroundColor: SIDEBAR_STYLE.userBubbleColor, color: '#fff',    maxWidth: '80%' } },
      ai:   { bubble: { backgroundColor: SIDEBAR_STYLE.botBubbleColor,  color: '#e5e7eb', maxWidth: '80%' } }
    }
  };

  // Hide Deep Chat's built-in input — we provide our own footer input
  el.inputAreaStyle = { display: 'none' };

  // Deep Chat sanitizes message HTML and strips class attributes, so the
  // .crafter-markdown selector prefix never matches inside the shadow DOM.
  // Stripping it here converts the scoped rules to bare selectors, which
  // work fine because auxiliaryStyle is already isolated to this shadow root.
  el.auxiliaryStyle = rawCrafterCss.replace(/\.crafter-markdown\s+/g, '') + '\n' + FORM_BUBBLE_CSS;

  // Manual loading-bubble control: required so we can hide it for silent responses
  // (e.g. handover/transfer where the backend forwards the message externally and
  // returns no chat reply). Without this, returning `[]` from responseInterceptor
  // leaves the loading bubble spinning forever.
  el.displayLoadingBubble = { toggle: () => {} };
  loadingBubbleShown = false;
  // Su errore la richiesta è chiusa: una risposta rapida in attesa può partire.
  // deep-chat toglie da sé la bolla (è un elemento temporaneo): va solo riallineato lo stato.
  el.onError = () => { turnoInCorso = false; loadingBubbleShown = false; };
  el.addEventListener('render', () => osservaCambiChat(el));

  // botId come query param: arriva sempre, anche su multipart/form-data per gli
  // allegati (dove additionalBodyProps/header possono non essere serializzati).
  el.connect = { url: `${API_BASE}/chat?botId=${encodeURIComponent(props.botId)}`, method: 'POST' };

  el.mixedFiles = { files: { maxNumberOfFiles: 10, acceptedFormats: 'image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' } };
  el.connect.additionalBodyProps = { botId: props.botId };

  // ── Request interceptor — mirrors widgetController.js buildDeepChat() ────────
  el.requestInterceptor = (reqDetails) => {
    const body = reqDetails.body;
    turnoInCorso = true;
    rapidaInAttesa = null;
    clearTimeout(rapidaTimer);

    // Messaggio con file: Deep Chat invia un FormData (files + message1 +
    // additionalBodyProps.botId). NON sovrascrivere il body; aggiungi solo il
    // conversationId dinamico come campo del form (botId è già presente).
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      el._wasNewConv = !conversationIdRef.value;
      if (conversationIdRef.value) body.append('conversationId', conversationIdRef.value);
      // Mostra la bolla di caricamento come fa il ramo testo: il responseInterceptor
      // la nasconde.
      setLoadingBubble(true);
      reqDetails.headers = { ...(reqDetails.headers || {}), ...tokenHeaders().headers };
      return reqDetails;
    }

    const msgs = (body && body.messages) || [];
    const lastMsg = msgs[msgs.length - 1] || {};

    el._wasNewConv = !conversationIdRef.value;

    reqDetails.body = {
      botId:          props.botId,
      message:        lastMsg.text || '',
      conversationId: conversationIdRef.value || undefined
    };

    if (pendingSelectedOptions.length > 0) {
      reqDetails.body.selectedOptions = pendingSelectedOptions.slice();
      pendingSelectedOptions = [];
    }

    // Show the loading bubble (manual toggle mode); responseInterceptor hides it.
    setLoadingBubble(true);

    reqDetails.headers = { ...(reqDetails.headers || {}), ...tokenHeaders().headers };
    return reqDetails;
  };

  // ── Response interceptor — mirrors widgetController.js buildDeepChat() ───────
  el.responseInterceptor = (response) => {
    isSending.value = false;
    turnoInCorso = false;
    // Hide the loading bubble shown by the requestInterceptor (già nascosta se nel
    // turno è arrivata una domanda con bottoni).
    setLoadingBubble(false);

    if (response?.error?.code === 'CONVERSATION_TOKEN_INVALID') {
      resetConversation();
      return { error: t('chatWidget.errorMessage') };
    }

    if (!response?.success) {
      return { error: response?.error?.message || t('chatWidget.errorMessage') };
    }

    if (response.conversationId) {
      const wasNewConv = el._wasNewConv;
      setConversation(response.conversationId, response.conversationToken || undefined);

      // Race-condition catch-up: fetch webhook messages that fired before we
      // joined the socket room (can only happen on the very first message).
      if (wasNewConv) {
        const convId = response.conversationId;
        api.get(`/chat/${convId}/messages`, tokenHeaders())
          .then(r => r.data)
          .then(msgs => {
            if (!Array.isArray(msgs) || !chatEl) return;
            msgs.forEach(m => {
              if (m.source === 'webhook' && m.text) {
                if (m.type === 'html') {
                  chatEl.addMessage({ html: sanitizeHtml(m.text), role: 'ai' });
                } else {
                  chatEl.addMessage({ text: m.text, role: 'ai' });
                }
              }
            });
          })
          .catch(() => {});
      }
    }

    const messages = response.messages || [];
    // Silent response (e.g. transfer/handover forwards the message externally and
    // doesn't reply in-chat). Returning an empty array tells Deep Chat to render
    // no bubble at all, instead of an empty one.
    if (messages.length === 0) return [];

    // Single plain-text message — return as text (Deep Chat renders markdown)
    if (messages.length === 1 && !messages[0].html && messages[0].type !== 'question') {
      return { text: messages[0].content || '' };
    }

    // Multiple messages or HTML — combine into a single HTML block
    const parts = messages.map(m => {
      if (m.html) {
        const textPart = m.content ? `<p style="margin:0 0 8px 0;">${escHtml(m.content)}</p>` : '';
        return textPart + m.html;
      }
      return `<p style="margin:0;">${escHtml(m.content)}</p>`;
    });

    return { html: parts.join('<hr style="border:none;border-top:1px solid #4b5563;margin:8px 0;">') };
  };

  // Aggancia un singolo wrapper searchselect. Usato sia dal focus top-level sia
  // dal focusin delegato sul contenitore array (Deep Chat non aggancia il focus
  // sugli input searchselect annidati dentro .newc-form-array). Idempotente
  // (wireSearchSelectField ignora i wrapper già agganciati).
  const wireSearchSelectWrapper = (wrapper) => {
    if (!wrapper) return;
    const labels = {
      searching: t('searchselect.runtime.searching'),
      empty: t('searchselect.runtime.empty'),
      error: t('searchselect.runtime.error'),
    };
    let search;
    if (wrapper.dataset.source === 'webhook') {
      const ref = wrapper.dataset.sourceRef || '';
      search = async (query) => {
        const res = await api.post('/chat/searchselect-search', {
          botId: props.botId,
          conversationId: conversationIdRef.value,
          ref,
          query,
        }, tokenHeaders());
        return (res.data && res.data.data && res.data.data.results) || [];
      };
    } else {
      search = makeSearchFn(wrapper.dataset.code || '');
    }
    wireSearchSelectField(wrapper, { debounceMs: 250, labels, search });
  };

  // Feedback al passaggio del mouse per i bottoni generati da noi. Deep Chat, quando
  // il mouse esce, AZZERA le proprietà dichiarate qui, sullo stesso attributo style
  // che porta il colore inline del bottone: perciò l'hover tocca 'filter' e non
  // backgroundColor, che resterebbe azzerato (bottone invisibile dopo il primo
  // passaggio — il difetto che dava la classe deep-chat-button).
  const NEWC_BTN_MOUSE_STYLES = {
    hover: { filter: 'brightness(0.92)' },
    click: { filter: 'brightness(0.85)' },
  };

  // ── Risposte rapide (bottoni, select, multiselect) — come widgetController.js ──
  // deep-chat scarta in silenzio submitUserMessage mentre una richiesta è in corso:
  // la domanda con bottoni di un tool arriva via socket prima che il turno HTTP sia
  // chiuso, e un click in quella finestra andava perso a bottoni già disattivati.
  // La risposta resta in attesa e si ritenta finché la richiesta non parte davvero.
  let turnoInCorso = false;
  let rapidaInAttesa = null;
  let rapidaTimer = null;
  const inviaRispostaRapida = (text) => {
    rapidaInAttesa = { text, tentativi: 0 };
    tentaRispostaRapida();
  };
  const tentaRispostaRapida = () => {
    clearTimeout(rapidaTimer);
    if (!rapidaInAttesa) return;
    if (!turnoInCorso) {
      rapidaInAttesa.tentativi++;
      el.submitUserMessage({ text: rapidaInAttesa.text });
    }
    // 150 ms × 70 ≈ 10 s di tentativi, poi si rinuncia
    if (rapidaInAttesa && rapidaInAttesa.tentativi < 70) rapidaTimer = setTimeout(tentaRispostaRapida, 150);
    else rapidaInAttesa = null;
  };

  // ── HTML class utilities — mirrors widgetController.js buildDeepChat() ───────
  el.htmlClassUtilities = {
    'newc-btn': {
      styles: NEWC_BTN_MOUSE_STYLES,
      events: {
        click(e) {
          if (chatInMovimento()) return;
          const value = e.target.getAttribute('data-value');
          if (!value) return;
          const container = e.target.closest('.newc-question-buttons');
          if (container) { container.style.pointerEvents = 'none'; container.style.opacity = '0.5'; }
          inviaRispostaRapida(value);
        }
      }
    },
    'newc-select': {
      events: {
        change(e) {
          const value = e.target.value;
          if (!value) return;
          const container = e.target.closest('.newc-question-select');
          if (container) { container.style.pointerEvents = 'none'; container.style.opacity = '0.5'; }
          inviaRispostaRapida(value);
        }
      }
    },
    'newc-multiselect-checkbox': {
      events: {
        change(e) {
          const { value, checked } = e.target;
          if (checked) {
            if (!pendingSelectedOptions.includes(value)) pendingSelectedOptions.push(value);
          } else {
            pendingSelectedOptions = pendingSelectedOptions.filter(v => v !== value);
          }
        }
      }
    },
    'newc-multiselect-confirm': {
      styles: NEWC_BTN_MOUSE_STYLES,
      events: {
        click(e) {
          if (chatInMovimento()) return;
          if (pendingSelectedOptions.length === 0) return;
          const container = e.target.closest('.newc-question-multiselect');
          if (container) { container.style.pointerEvents = 'none'; container.style.opacity = '0.5'; }
          inviaRispostaRapida(pendingSelectedOptions.join(', '));
        }
      }
    },
    'newc-form-array': {
      events: {
        click(e) {
          const add = e.target.closest('.newc-array-add');
          if (add) { addArrayRow(add); return; }
          const rem = e.target.closest('.newc-array-remove');
          if (rem) removeArrayRow(rem);
        },
        // I searchselect dentro le righe array non ricevono il focus-wiring
        // top-level: li agganciamo qui via focusin delegato (copre riga iniziale
        // e righe aggiunte dinamicamente).
        focusin(e) {
          const input = e.target && e.target.closest && e.target.closest('.newc-form-searchselect-input');
          if (!input) return;
          wireSearchSelectWrapper(input.closest('.newc-form-searchselect'));
        }
      }
    },
    // Ripulisce l'errore del campo appena corretto: senza questo il messaggio
    // resterebbe sotto un campo ormai valido fino al submit successivo.
    'newc-form': {
      events: {
        // Un <form> i cui bottoni sono tutti type="button" può comunque essere
        // inviato dal browser: con un solo campo di testo, Invio fa partire la
        // submission implicita, cioè una navigazione. Qui la si ferma e si
        // instrada sullo stesso percorso del bottone Invia.
        submit(e) {
          e.preventDefault();
          const f = e.target.closest ? e.target.closest('.newc-form') : e.target;
          const btn = f && f.querySelector('.newc-form-submit');
          if (btn && !btn.disabled) btn.click();
        },
        input(e) {
          const el2 = e.target;
          if (!el2 || !el2.classList) return;
          if (el2.classList.contains('newc-form-field') || el2.classList.contains('newc-array-field')
            || el2.classList.contains('newc-form-address-input') || el2.classList.contains('newc-form-searchselect-input')) {
            clearFieldError(el2);
          }
        }
      }
    },
    'newc-form-submit': {
      styles: NEWC_BTN_MOUSE_STYLES,
      events: {
        click(e) {
          const form = e.target.closest('.newc-form');
          if (!form) return;
          const { values, missing } = collectFormValues(form, formErrorLabels(t));
          if (missing) {
            // Porta l'utente sul primo campo da correggere IN ORDINE DI LETTURA:
            // il container di un array porta anch'esso la classe di invalido ma
            // non è focalizzabile, quindi si scartano i non-campo.
            const first = form.querySelector('.newc-form-field.newc-form-invalid, .newc-array-field.newc-form-invalid');
            if (first) {
              const wrap = first.closest('.newc-form-address') || first.closest('.newc-form-searchselect');
              const focusEl = wrap
                ? (wrap.querySelector('.newc-form-address-input') || wrap.querySelector('.newc-form-searchselect-input'))
                : first;
              if (focusEl) {
                focusEl.focus();
                if (focusEl.scrollIntoView) focusEl.scrollIntoView({ block: 'nearest' });
              }
            }
            return;
          }
          form.querySelectorAll('.newc-form-field, .newc-form-submit').forEach(el => { el.disabled = true; });
          form.style.opacity = '0.6';
          api.post('/chat/form-submit', {
            botId: props.botId,
            conversationId: conversationIdRef.value,
            messageId: form.dataset.formId,
            values
          }, tokenHeaders()).then(() => { closeForm(form); })
            .catch(() => {
              form.querySelectorAll('.newc-form-field, .newc-form-submit').forEach(el => { el.disabled = false; });
              form.style.opacity = '1';
              form.classList.add('newc-form-invalid');
            });
        }
      }
    }
    ,'newc-form-cancel': {
      styles: NEWC_BTN_MOUSE_STYLES,
      events: {
        click(e) {
          const form = e.target.closest('.newc-form');
          if (!form) return;
          form.querySelectorAll('.newc-form-field, .newc-form-submit, .newc-form-cancel').forEach(el => { el.disabled = true; });
          form.style.opacity = '0.6';
          api.post('/chat/form-cancel', {
            botId: props.botId,
            conversationId: conversationIdRef.value,
            messageId: form.dataset.formId
          }, tokenHeaders()).then(() => { closeForm(form); })
            .catch(() => {
              form.querySelectorAll('.newc-form-field, .newc-form-submit, .newc-form-cancel').forEach(el => { el.disabled = false; });
              form.style.opacity = '1';
              form.classList.add('newc-form-invalid');
            });
        }
      }
    }
    ,'newc-form-address-input': {
      events: {
        focus(e) {
          const wrapper = e.target.closest('.newc-form-address');
          if (!wrapper) return;
          wireAddressField(wrapper, {
            debounceMs: 900,
            labels: {
              searching: t('address.runtime.searching'),
              empty: t('address.runtime.empty'),
              error: t('address.runtime.error'),
              civicoLabel: t('address.civico.label'),
              civicoPlaceholder: t('address.civico.placeholder'),
              civicoRequired: t('address.civico.required'),
            },
            search: async (query) => {
              const res = await api.post('/chat/address-search', {
                botId: props.botId,
                conversationId: conversationIdRef.value,
                query,
              }, tokenHeaders());
              return (res.data && res.data.data && res.data.data.results) || [];
            },
          });
        }
      }
    }
    ,'newc-form-searchselect-input': {
      events: {
        focus(e) {
          wireSearchSelectWrapper(e.target.closest('.newc-form-searchselect'));
        }
      }
    }
  };

  return el;
}

async function loadAndShowWelcome(el) {
  try {
    // Open-panel handshake: create the conversation up front so we hold its id
    // (and join the socket room via conversationIdRef) BEFORE the first turn.
    // This is what makes courtesy sendMessage events on later turns arrive live
    // instead of racing the emit against a not-yet-joined room. The backend also
    // returns the welcome / initial-flow output (translated) as ordered history.
    const browserLang = (navigator.language || '').split('-')[0].toLowerCase() || undefined;
    const res = await chatService.initConversation({
      botId: props.botId,
      conversationId: conversationIdRef.value || undefined,
      conversationToken: conversationTokenRef.value,
      initLanguage: browserLang
    });
    if (res?.conversationId) {
      setConversation(res.conversationId, res.conversationToken || undefined); // triggers useIncomingMessages → join room
    }
    const msgs = Array.isArray(res?.messages) ? res.messages : [];
    if (msgs.length > 0) {
      welcomeSent = true;
      // Set as initial history before the element mounts — more reliable than
      // the 'render' event which may not fire consistently in connect mode.
      el.history = msgs.map(m => {
        const role = m.role === 'user' ? 'user' : 'ai';
        if (m.attachments && m.attachments.length) {
          const files = m.attachments.map(a => ({
            src: a.url, name: a.filename || 'file',
            type: (a.mimetype || '').startsWith('image') ? 'image' : 'file',
          }));
          return m.text ? { files, text: m.text, role } : { files, role };
        }
        if (m.type === 'html' || m.type === 'form') return { html: sanitizeHtml(m.text), role };
        return { text: m.text, role };
      });
      // Attivo solo l'ultimo form, se ancora da compilare — anche quando dopo c'è
      // un testo del modello.
      const lastForm = [...msgs].reverse().find(m => m.type === 'form');
      const pending = lastForm && (lastForm.interactiveStatus
        ? lastForm.interactiveStatus === 'pending'
        : !lastForm.formConfig?.values);
      activeFormId = pending ? formIdOf(lastForm) : null;
      formPending.value = !!activeFormId;
      // Lo storico si monta dopo: i form vecchi vanno spenti a DOM pronto.
      el.addEventListener('render', () => setTimeout(syncForms, 0));
    }
  } catch {
    // Fail silently — chat still works without the init handshake
  }
}

async function runChatroomScripts() {
  if (!widgetScripts.customScript && !widgetScripts.testScript) return;
  // Prenota un conversationId così addContext() può allegare il context PRIMA del primo turno.
  try {
    const res = await api.post('/chat/reserve', { botId: props.botId });
    if (res.data?.conversationId) setConversation(res.data.conversationId, res.data.conversationToken || null);
  } catch { /* fallback: creazione lazy al primo messaggio */ }
  if (!conversationIdRef.value) return;

  if (activeSandbox) { activeSandbox.destroy(); activeSandbox = null; }
  const sandbox = runCustomScriptSandbox({
    apiBaseUrl: API_BASE,
    conversationId: conversationIdRef.value,
    conversationToken: conversationTokenRef.value,
    customScript: widgetScripts.customScript,
    testScript: widgetScripts.testScript,
    onSendMessage: (text) => { if (chatEl) chatEl.submitUserMessage({ text: String(text) }); },
    onNewConversation: () => { resetConversation(); },
  });
  activeSandbox = sandbox;
  await sandbox.ready; // attende il completamento degli addContext (o timeout)
}

async function mountDeepChat() {
  if (!deepChatWrapRef.value) return;
  chatEl = buildDeepChat();
  // 1) prenota conversazione + esegue custom/test script (context iniettato prima del welcome)
  await runChatroomScripts();
  // 2) handshake welcome/initial-flow: riusa il conversationId prenotato, ora col context presente
  await loadAndShowWelcome(chatEl);
  // The awaits above yield; the user can navigate away and unmount the sidebar
  // before we reach here, nulling the ref. Bail rather than appendChild on null.
  if (!deepChatWrapRef.value) return;
  deepChatWrapRef.value.appendChild(chatEl);
}

async function resetConversation() {
  if (activeSandbox) { activeSandbox.destroy(); activeSandbox = null; }
  if (chatEl && deepChatWrapRef.value?.contains(chatEl)) {
    deepChatWrapRef.value.removeChild(chatEl);
    chatEl = null;
  }
  setConversation(null, null);
  pendingSelectedOptions = [];
  pendingFiles.value = [];
  welcomeSent = false;
  isSending.value = false;
  formPending.value = false;
  activeFormId = null;
  await mountDeepChat();
}

function sendMessage() {
  const text = messageText.value.trim();
  const files = pendingFiles.value.slice();
  if ((!text && !files.length) || isSending.value || !chatEl) return;
  messageText.value = '';
  pendingFiles.value = [];
  isSending.value = true;
  const payload = { text };
  if (files.length) payload.files = files;
  chatEl.submitUserMessage(payload);
}

// The footer input is `disabled` while a turn is in flight, a form is pending or the
// agent is typing. Disabling an element blurs it and the browser never gives focus back,
// so the user had to click the input again after every message. Restore focus once every
// blocking flag clears — only if focus was inside the footer when it got blocked and
// nothing else has taken it since (never steal the caret from a node editor the user
// moved to while waiting for the reply).
const inputBlocked = computed(() => isSending.value || formPending.value || agentTyping.value);
let refocusWhenUnblocked = false;
watch(inputBlocked, (blocked) => {
  if (blocked) {
    // Pre-flush: the DOM isn't patched yet, so the input/button still holds focus here.
    refocusWhenUnblocked = !!footerFormRef.value?.contains(document.activeElement);
    return;
  }
  if (!refocusWhenUnblocked) return;
  refocusWhenUnblocked = false;
  nextTick(() => {
    const active = document.activeElement;
    if (active && active !== document.body && !footerFormRef.value?.contains(active)) return;
    inputRef.value?.focus();
  });
});

// "Test nella chat" dall'editor del nodo "Invia messaggio in web chat":
// riceve l'HTML già renderizzato dall'editor e lo mostra in deep-chat,
// con la stessa sanitizzazione del percorso socket type 'html'.
// detail.handled segnala all'editor che la sidebar ha ricevuto (dispatch sincrono).
const onTestHtmlMessage = (e) => {
  if (!chatEl || !e.detail?.html) return;
  isCollapsed.value = false;
  chatEl.addMessage({ html: sanitizeHtml(e.detail.html), role: 'ai' });
  e.detail.handled = true;
};

onMounted(async () => {
  window.addEventListener('newc:test-html-message', onTestHtmlMessage);
  try {
    const saved = localStorage.getItem('chatSidebarCollapsed');
    if (saved !== null) isCollapsed.value = saved === 'true';
  } catch {}

  await loadDeepChat();
  await loadWidgetScripts();
  await mountDeepChat();
  inputRef.value?.focus();
  loadProcessingDocs();
});

onBeforeUnmount(() => {
  window.removeEventListener('newc:test-html-message', onTestHtmlMessage);
  if (activeSandbox) { activeSandbox.destroy(); activeSandbox = null; }
  if (chatEl) { chatEl.remove(); chatEl = null; }
});

const handleCloseTranscript = () => {
  closeTranscript();
};

watch(activeTranscriptConvId, async (convId) => {
  transcriptCopied.value = false;
  if (convId !== null) {
    isCollapsed.value = false;
    transcriptFlash.value = false;
    await nextTick();
    transcriptFlash.value = true;
    setTimeout(() => { transcriptFlash.value = false; }, 1500);
    // Save snapshot only if not already in transcript mode (i.e., first transcript loaded)
    if (snapshotConversationId.value === null) {
      snapshotConversationId.value = conversationIdRef.value;
      snapshotConversationToken.value = conversationTokenRef.value;
    }
    // Tear down existing Deep Chat and rebuild with transcript history
    if (chatEl && deepChatWrapRef.value?.contains(chatEl)) {
      deepChatWrapRef.value.removeChild(chatEl);
      chatEl = null;
    }
    chatEl = buildDeepChat();
    chatEl.history = transcriptMessages.value.map(m => {
      const meta = [];
      if (m.createdAt) {
        meta.push(new Date(m.createdAt).toLocaleString());
      }
      if (m.role === 'ai' && m.source) {
        const sourceLabel = t(`botEditor.transcripts.source.${m.source}`) || m.source;
        meta.push(`${t('botEditor.chatSidebar.transcriptMode.origin')} — ${sourceLabel}`);
      }
      if (m.attachments && m.attachments.length) {
        const files = m.attachments.map(a => ({
          src: a.url, name: a.filename || 'file',
          type: (a.mimetype || '').startsWith('image') ? 'image' : 'file',
        }));
        return m.text ? { files, text: m.text, role: m.role } : { files, role: m.role };
      }
      if (meta.length === 0) {
        if (m.type === 'html') return { html: sanitizeHtml(m.text || ''), role: m.role };
        return { text: m.text, role: m.role };
      }
      const metaHtml = meta.map(line => `<small style="opacity:0.55;font-size:0.72em;display:block;margin-top:2px">${escHtml(line)}</small>`).join('');
      const bodyHtml = m.type === 'html'
        ? sanitizeHtml(m.text || '')
        : `<div class="crafter-markdown">${renderContent(m.text)}</div>`;
      return {
        html: `${bodyHtml}<br>${metaHtml}`,
        role: m.role
      };
    });
    if (!deepChatWrapRef.value) return; // ref may be null if unmounted during the awaits above
    deepChatWrapRef.value.appendChild(chatEl);
  } else {
    // Closing transcript mode
    if (snapshotConversationId.value) {
      const snapId = snapshotConversationId.value;
      snapshotConversationId.value = null;
      const snapToken = snapshotConversationToken.value;
      snapshotConversationToken.value = null;
      // Clear any staged files that may have accumulated while in transcript mode
      pendingFiles.value = [];
      // Tear down transcript Deep Chat
      if (chatEl && deepChatWrapRef.value?.contains(chatEl)) {
        deepChatWrapRef.value.removeChild(chatEl);
        chatEl = null;
      }
      // Restore snapshot conversation
      try {
        const msgs = await api.get(`/chat/${snapId}/messages`, conversationTokenHeaders(snapToken)).then(r => r.data).catch(() => []);
        chatEl = buildDeepChat();
        if (Array.isArray(msgs) && msgs.length > 0) {
          chatEl.history = msgs.map(m => {
            const role = m.role === 'assistant' ? 'ai' : 'user';
            if (m.attachments && m.attachments.length) {
              const files = m.attachments.map(a => ({
                src: a.url, name: a.filename || 'file',
                type: (a.mimetype || '').startsWith('image') ? 'image' : 'file',
              }));
              return m.text ? { files, text: m.text, role } : { files, role };
            }
            if (m.type === 'html') return { html: sanitizeHtml(m.text || ''), role };
            return { text: m.text || '', role };
          });
        }
        setConversation(snapId, snapToken);
        if (!deepChatWrapRef.value) return; // ref may be null if unmounted during the awaits above
        deepChatWrapRef.value.appendChild(chatEl);
      } catch {
        await resetConversation();
      }
    } else {
      await resetConversation();
    }
  }
});

// When navigating to a different bot, start a fresh session and reload processing state
watch(() => props.botId, async () => {
  await loadWidgetScripts();
  resetConversation();
  _processingIds.clear();
  processingCount.value = 0;
  loadProcessingDocs();
});
</script>

<style scoped>
@keyframes transcript-loaded {
  0%        { opacity: 0; }
  12%, 28%  { opacity: 0.12; }
  42%       { opacity: 0; }
  55%, 68%  { opacity: 0.07; }
  80%       { opacity: 0; }
  90%, 97%  { opacity: 0.03; }
  100%      { opacity: 0; }
}

.transcript-loaded-flash {
  background-color: rgb(34 211 238);
  animation: transcript-loaded 1.5s ease forwards;
}
</style>
